// The enemies' brains (gate B4): what the behaviour trees (@rpg-unit/enemy-archetypes' `behaviour-trees` table) may
// name. The vocabulary is @rpg/ai's standard brains (moved there from this file, 2026-10-20): each condition, action and
// scorer reads one enemy's view of the arena (`Brain`) and acts only through its ports: the crowd (where to move), the
// attack-token pool, and the combat port's `request` (never combat's internals). This file is the game's side: its
// enemy state, its archetype as the fight reads it, and the context's concrete ports.
import { standardBrains, STANDARD_BRAIN_NAMES, type BehaviourRegistry, type BrainContext, type PerceptionState, type SenseTuning } from "@rpg/ai";
import type { NavCrowd } from "@rpg/nav";
import type { FighterView, IntentKind } from "@rpg/combat";

/** [x, y, z]: plain data in the saved state. */
export type Vec3 = [number, number, number];

/** One enemy's saved state. */
export interface EnemyState {
  id: number;
  kind: string;
  /** 1, or a boss's later phase (2…). */
  phase: number;
  alive: boolean;
  /** Seconds since defeat. */
  deadFor: number;
  home: Vec3;
  pos: Vec3;
  vel: Vec3;
  facing: number;
  percept: PerceptionState;
  tree: import("@rpg/ai").TreeState;
  /** The step it last sensed on (noises since then reach it). */
  lastThink: number;
  /** Who hit it since its last think, and from where. */
  hitBy: number | null;
  hitFrom: Vec3 | null;
  wanderAt: Vec3 | null;
  wanderWait: number;
  /** A boss's summon used in its current phase. */
  summoned: boolean;
}

/** An enemy kind's numbers as the brains read them (the enemies table's row, references resolved). */
export interface Archetype {
  id: string;
  card: string;
  tree: string;
  hp: number;
  poise: number;
  body: { radius: number; height: number };
  senses: SenseTuning;
  speed: { walk: number; run: number; accel: number; turn: number; separation: number };
  /** Its combat weapon (the move set), and the intents it answers: the combo's first move for `attack`, then the skill
   *  and the big move (read off the weapon). */
  weapon: string;
  moveset: Partial<Record<IntentKind, string>>;
  /** @rpg/combat's attack and defence for the kind. */
  stats: { attack: number; defence: number };
  tokenPriority: number;
  noise: number;
  phases: { atHp: number; card: string; weapon: string; moveset: Partial<Record<IntentKind, string>>; speedScale: number; summon: { kind: string; count: number } | null }[];
}

/** What a brain sees and can do, for one enemy on one tick: @rpg/ai's context with this game's ports (the crowd is
 *  @rpg/nav's, the combat port @rpg/combat's by intent). */
export interface Brain extends BrainContext<EnemyState, Archetype> {
  crowd: NavCrowd;
  combat: { request(entity: number, intent: { kind: IntentKind; dir?: Vec3 }): boolean; state(entity: number): FighterView | null; setTarget(entity: number, target: number | null): void };
  /** The moveset now (its phase's). */
  moveset: Partial<Record<IntentKind, string>>;
}

/** The vocabulary the trees use: @rpg/ai's standard brains (moved there from this file: the actions, conditions and
 *  scorers act only through the context's ports), typed for this game's context. */
export const BRAINS: BehaviourRegistry<Brain> = standardBrains<Brain>();

/** The names the trees may use (the content check validates every tree against them). */
export const BRAIN_NAMES = STANDARD_BRAIN_NAMES;
